'use client';
import IconArrowDown from '@/assets/icons/category-second-level/icon-arrow-down-second.svg';
import { CardSecond, CategoryCard, CustomCarouselCategory } from '@/components';

import { SelectedAttributeType } from '@/components/ui/card-second';
import Pagination from '@/components/ui/pagination';
import {
  AttributeTypeEnum,
  OrderDirection,
  ProductOrderField,
  ProductVariantSortingInput,
  useAttributesCategoriesQuery,
  useBrandListQuery,
  useCategoryQuery,
  useGetProductsQuery,
  useProductVariantsQuery,
} from '@/graphql';
import { decodeBase64Id } from '@/helpers/decodeBase64Id';
import useHasMounted from '@/hooks/useHasMounted';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { usePathname } from '@/i18n/navigation';
import { useCityStore } from '@/zustand/cityStore';
import { useLocale, useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import {
  ButtonHTMLAttributes,
  ClassAttributes,
  JSX,
  LegacyRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Skeleton from 'react-loading-skeleton';
import { useWindowSize } from 'react-use';
import styles from './category-second.module.scss';
import SecondContentDrawer from './drawer';
import SecondContentFilter from './filter';
import SecondContentHeader from './header';
import SecondContentLiderSellCategory from './liderSellCategory';
import {
  AttributeTypes,
  BrandType,
  BreadcrumbItem,
  CategorySecondContentProps,
  ProductType,
  SortOrder,
} from './secondContent.types';
import SecondContentTabs from './tabs';

const renderButton = (
  props: JSX.IntrinsicAttributes &
    ClassAttributes<HTMLButtonElement> &
    ButtonHTMLAttributes<HTMLButtonElement>,
  ref: LegacyRef<HTMLButtonElement> | undefined
) => {
  const t = useTranslations('General');
  return (
    <button {...props} ref={ref} className={styles.customBtn}>
      {t('reccomendation')} <IconArrowDown />
    </button>
  );
};

const CategorySecondContent: React.FC<CategorySecondContentProps> = ({
  categoryId,
}) => {
  const params = useParams();
  const searchParams = useSearchParams();

  const paramSlug = params.slug;
  const paramSort = searchParams.get('sort');
  const sorted =
    paramSort === ProductOrderField.POPULARITY
      ? {
          field: ProductOrderField.POPULARITY,
          direction: OrderDirection.DESC,
        }
      : null;

  const [productsSortBy, setProductsSortBy] = useState<SortOrder | null>(
    sorted
  );
  const [productsVariantsSortBy, setProductsVariantsSortBy] =
    useState<ProductVariantSortingInput | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productsVariants, setProductsVariants] = useState<any[]>([]);
  const [showSkeletons, setShowSkeletons] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [count, setCount] = useState(1);
  const [prev, setPrev] = useState<string | null | undefined>(null);
  const [next, setNext] = useState<string | null | undefined>(null);
  const hasMounted = useHasMounted();
  const [cardsView, setCardsView] = useState('full');
  const [openMobileVersion, setOpenMobileVersion] = useState(false);
  const pathname = usePathname();
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const languageCode = useLanguageCode();
  const locale = useLocale();
  const [selectedAttribute, setSelectedAttribute] = useState<
    { slug: string; values: string[] }[]
  >([]);
  const [filterByPrice, setFilterByPrice] = useState<number[]>([]);
  const [percentDiscountAmount, setPercentDiscountAmount] = useState<
    string | number | undefined
  >(undefined);

  const tBreadcrumb = useTranslations('Breadcrumbs');
  const [breadData, setBreadData] = useState<BreadcrumbItem[]>([
    {
      title: tBreadcrumb('main'),
      link: '/',
    },
  ]);

  const [variantsIds, setVariantIds] = useState<string[]>([]);
  const [isOnSale, setIsOnSale] = useState(false);
  const [isAvailableForInstallment, setIsAvailableForInstallment] =
    useState(false);

  const { width } = useWindowSize();
  const { selectedCity } = useCityStore();

  const productsPerPage = () => {
    if (width <= 1130) {
      return 6;
    }
    return cardsView === 'full' ? 6 : 12;
  };

  const { data, loading } = useCategoryQuery({
    variables: {
      languageCode: languageCode,
      id: categoryId,
      first: 10,
      productFilter: {
        ids: selectedProductIds,
      },
    },
  });

  useEffect(() => {
    if (data) {
      const filteredIds = data?.category?.products?.edges
        .flatMap(edge => edge.node.variants?.map(variant => variant.id) ?? [])
        .filter((id): id is string => typeof id === 'string');

      if (filteredIds) {
        setVariantIds(filteredIds);
      }
    }
  }, [data]);

  const { data: productsVariantsData, loading: variantLoading } =
    useProductVariantsQuery({
      variables: {
        languageCode: languageCode,
        city: selectedCity,
        ...(next
          ? { first: productsPerPage(), after: next }
          : prev
            ? { last: productsPerPage(), before: prev }
            : { first: productsPerPage() }),
        filter: {
          productIds: selectedProductIds,
          category: categoryId,
          availableForInstallment: isAvailableForInstallment,
          onSale: isOnSale,
          attributes:
            selectedAttribute.length > 0 ? selectedAttribute : undefined,
          brands: selectedBrands.length > 0 ? selectedBrands : undefined,
          price:
            filterByPrice.length > 0
              ? {
                  gte: filterByPrice[0],
                  lte: filterByPrice[1],
                }
              : undefined,
        },
        sortBy: productsVariantsSortBy,
      },
      skip: variantsIds.length === 0,
    });

  const discountAmounts = productsVariantsData?.productVariants?.edges
    ?.filter(item => !!item?.node?.pricing?.percentageDiscountAmount)
    .map(
      item =>
        Math.floor(item?.node?.pricing?.percentageDiscountAmount / 10) * 10
    );

  const filteredProductsByDiscountAmount =
    productsVariantsData?.productVariants?.edges?.filter(item => {
      if (percentDiscountAmount === 'Все') {
        return !!item?.node?.pricing?.percentageDiscountAmount;
      } else {
        return (
          Number(percentDiscountAmount) <=
          Number(
            Math.floor(item?.node?.pricing?.percentageDiscountAmount / 10) * 10
          )
        );
      }
    }) || [];

  const productsToRender =
    (filteredProductsByDiscountAmount.length > 0 ||
      filteredProductsByDiscountAmount.length === 0) &&
    !!percentDiscountAmount
      ? filteredProductsByDiscountAmount
      : productsVariantsData?.productVariants?.edges || [];

  const { data: productsData, loading: productsLoading } = useGetProductsQuery({
    variables: {
      first: 10,
      languageCode: languageCode,
      filter: {
        categories: [categoryId],
      },
    },
  });

  const { data: brandData } = useBrandListQuery({
    variables: {
      first: 10,
      forCategory: categoryId,
      languageCode: languageCode,
    },
  });

  const { data: attributesData } = useAttributesCategoriesQuery({
    variables: {
      first: 10,
      languageCode: languageCode,
      where: {
        inCategory: categoryId,
        filterableInDashboard: true,
        type: {
          eq: AttributeTypeEnum.PRODUCT_TYPE,
        },
      },
    },
  });

  const attributes = attributesData?.attributesCategories?.edges.flatMap(
    attr => attr.node.attributes?.edges
  );

  useEffect(() => {
    setIsMobileView(width <= 1130);
  }, [width]);

  const openMobileMenu = () => setOpenMobileVersion(!openMobileVersion);

  const closeMobileMenu = () => setOpenMobileVersion(false);

  const handleChange = (viewType: string) => setCardsView(viewType);

  useEffect(() => {
    if (data?.category) {
      const newBreadData: BreadcrumbItem[] = [
        {
          title: tBreadcrumb('main'),
          link: '/',
        },
      ];

      const ancestor = data?.category?.ancestors?.edges
        ?.map(edge => edge?.node)
        ?.sort((a, b) => a.level - b.level);

      if (ancestor?.length == 2) {
        ancestor?.forEach(node => {
          const id = decodeBase64Id(node?.id);
          let link = '';

          if (node.level === 0) {
            link = `/catalog/${id}-${node.slug}`;
          } else if (node.level === 1) {
            link = `/category/${id}-${node.slug}`;
          }

          newBreadData.push({
            title:
              locale === 'ru'
                ? (node.name ?? 'Unknown')
                : (node?.translation?.name ?? ''),
            link,
          });
        });
      } else if (ancestor?.length == 1 && ancestor[0]?.level === 0) {
        newBreadData.push({
          title:
            locale === 'ru'
              ? (ancestor[0]?.name ?? 'Unknown')
              : (ancestor[0]?.translation?.name ?? ''),
          link: `/catalog/${decodeBase64Id(ancestor[0]?.id)}-${ancestor[0]?.slug}`,
        });
      }
      if (paramSlug !== 'topsellers') {
        newBreadData.push({
          title:
            locale === 'ru'
              ? data.category.name
              : (data?.category?.translation?.name as string),
          link: pathname,
        });
      }
      if (paramSlug === 'topsellers') {
        newBreadData.push({
          title: 'Лидеры продаж в категории',
          link: pathname,
        });
      }
      setBreadData(newBreadData);
    }
  }, [productsVariants, pathname]);
  const handleSortBy = (
    sortByCategory: SortOrder,
    sortByVariants: ProductVariantSortingInput
  ) => {
    setProductsSortBy(sortByCategory);
    setProductsVariantsSortBy(sortByVariants);
    setPrev(null);
    setNext(null);
    setCount(1);
  };

  const handleMiniCardClick = useCallback((productId: string) => {
    setSelectedProductIds(prevIds => {
      if (prevIds.includes(productId)) {
        return prevIds.filter(id => id !== productId);
      } else {
        return [...prevIds, productId];
      }
    });
    setPrev(null);
    setNext(null);
    setCount(1);
  }, []);

  const isProductSelected = useCallback(
    (productId: string) => {
      return selectedProductIds.includes(productId);
    },
    [selectedProductIds]
  );

  useEffect(() => {
    if (selectedProductIds.length > 0 && data?.category?.products?.edges) {
      const variants: any[] = [];
      data.category.products.edges.forEach(productEdge => {
        if (selectedProductIds.includes(productEdge.node.id)) {
          if (productEdge.node.variants) {
            variants.push(...productEdge.node.variants);
          }
        }
      });
      setProductsVariants(variants);
    } else {
      setProductsVariants([]);
    }
  }, [selectedProductIds, data?.category?.products?.edges]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (loading) {
      setShowSkeletons(true);
    } else {
      timer = setTimeout(() => {
        setShowSkeletons(false);
      }, 200);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const [additionalItemsCount, setAdditionalItemsCount] = useState(0);

  const getInitialItemsCount = () => {
    if (cardsView === 'card') return 12;
    if (cardsView === 'full') return 6;
    if (isMobileView) return 4;
    return 0;
  };

  const getBottomItemsCount = () => {
    return cardsView === 'card' ? 4 : 2;
  };

  const calculateDisplayedCount = () => {
    return getInitialItemsCount() + additionalItemsCount;
  };

  const calculateBottomSlice = () => {
    const initialCount = getInitialItemsCount();
    return {
      start:
        initialCount +
        (additionalItemsCount === 0
          ? additionalItemsCount
          : getBottomItemsCount()),
      end: initialCount + additionalItemsCount + getBottomItemsCount(),
    };
  };

  const handleClearFilters = () => {
    setSelectedProductIds([]);
    setAdditionalItemsCount(0);
  };

  const handleChangeBrands = (brand: string) => {
    const isSelected = selectedBrands.includes(brand);
    if (isSelected) {
      setSelectedBrands(prev => prev.filter((item: string) => item !== brand));
    } else {
      setSelectedBrands(prev => [...prev, brand]);
    }
  };

  const getProducts = () => {
    return selectedProductIds.length === 0
      ? data?.category?.products?.edges.map((p, index) => ({
          ...p?.node?.variants?.[0],
          key: p?.node?.id || index,
        }))
      : productsVariants.map((variant, index) => ({
          ...variant,
          key: variant?.id || index,
        }));
  };

  const products = getProducts();

  const displayedProductsCount =
    getInitialItemsCount() + additionalItemsCount + getBottomItemsCount();

  const displayedProducts = products?.slice(0, displayedProductsCount) || [];

  if (!hasMounted) return null;

  const totalProducts = productsVariantsData?.productVariants?.totalCount || 0;

  const totalPages = Math.ceil(totalProducts / getInitialItemsCount());

  const nextPage = () => {
    setNext(productsVariantsData?.productVariants?.pageInfo?.endCursor);
    setPrev(null);
  };

  const prevPage = () => {
    setPrev(productsVariantsData?.productVariants?.pageInfo?.startCursor);
    setNext(null);
  };

  const handleChangePrice = (value: [number, number]) =>
    setFilterByPrice(value);

  const handleChangeAttribute = (
    attribute: AttributeTypes,
    attr: Record<'id' | 'name' | 'slug', string>
  ) => {
    const attributeSlug = attribute?.node.slug;
    const valueSlug = attr?.slug;

    setSelectedAttribute(prev => {
      const existingAttrIndex = prev.findIndex(a => a.slug === attributeSlug);

      if (existingAttrIndex > -1) {
        const attrValues = prev[existingAttrIndex].values;
        const valueIndex = attrValues.indexOf(valueSlug);

        if (valueIndex > -1) {
          // Remove the value if it exists
          const updatedValues = attrValues.filter(v => v !== valueSlug);
          if (updatedValues.length === 0) {
            // Remove the entire attribute if no values left
            return prev.filter((_, i) => i !== existingAttrIndex);
          } else {
            // Update the attribute with remaining values
            const updated = [...prev];
            updated[existingAttrIndex] = {
              ...updated[existingAttrIndex],
              values: updatedValues,
            };
            return updated;
          }
        } else {
          // Add new value to existing attribute
          const updated = [...prev];
          updated[existingAttrIndex] = {
            ...updated[existingAttrIndex],
            values: [...attrValues, valueSlug],
          };
          return updated;
        }
      } else {
        // Add new attribute with value
        return [...prev, { slug: attributeSlug, values: [valueSlug] }];
      }
    });
  };

  const handleChangePercentDiscountAmount = (
    value: string | number | undefined
  ) => {
    setShowSkeletons(true);
    setPercentDiscountAmount(prev => (prev === value ? undefined : value));
    setTimeout(() => {
      setShowSkeletons(false);
    }, 2000);
  };

  const sm = width <= 768;
  const lg = width <= 1240;
  const xl = width >= 1240;

  const getCardsCount = (view: string) => {
    if (view === 'card') {
      if (sm) return 10;
      if (lg) return 9;
      if (xl) return 8;
      return 4;
    }
    return view === 'full' ? 4 : 0;
  };

  let totalCounts;
  if (percentDiscountAmount && filteredProductsByDiscountAmount.length > 0) {
    totalCounts = filteredProductsByDiscountAmount.length;
  } else if (
    percentDiscountAmount &&
    filteredProductsByDiscountAmount.length === 0
  ) {
    totalCounts = 0;
  } else {
    totalCounts = productsVariantsData?.productVariants?.totalCount;
  }

  return (
    <div className="container">
      <div className={styles.secondContentWrapper}>
        <SecondContentHeader
          loading={variantLoading}
          showSkeletons={showSkeletons}
          breadData={breadData}
          totalCount={totalCounts}
          selectedProductIds={selectedProductIds}
          productsVariants={productsVariants?.length || 0}
          categoryName={
            locale === 'ru'
              ? data?.category?.name
              : data?.category?.translation?.name
          }
          isTopSeller={paramSlug === 'topsellers'}
        />

        <div className={styles.cardsFilterWrapper}>
          <div className={styles.cards}>
            <SecondContentTabs
              productsLoading={productsLoading}
              productsData={productsData}
              handleMiniCardClick={handleMiniCardClick}
              isProductSelected={isProductSelected}
              handleSortBy={handleSortBy}
              productsSortBy={productsSortBy}
              handleChange={handleChange}
              openMobileMenu={openMobileMenu}
              renderButton={renderButton}
              cardsView={cardsView}
            />

            {cardsView === 'card' &&
              (variantLoading || showSkeletons ? (
                <div className={styles.cardsTypeList}>
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className={`${styles.skeletonForCardSecond}`}
                    >
                      <Skeleton />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.cardsTypeList}>
                  {(productsToRender?.length > 8
                    ? productsToRender.slice(0, 8)
                    : productsToRender
                  ).map(p => {
                    const firstVariant = p?.node;
                    const labels =
                      firstVariant?.labels?.edges?.map(
                        (edge: any) => edge?.node
                      ) || [];
                    const imageUrl = firstVariant?.media?.[0]?.url;

                    return (
                      <CategoryCard
                        id={firstVariant?.id}
                        categorySlug={p?.node?.product?.category?.slug}
                        slug={p?.node?.product?.slug}
                        productId={firstVariant?.product?.id}
                        key={firstVariant?.id}
                        image={imageUrl}
                        wide={true}
                        productType={
                          locale === 'ru'
                            ? p?.node?.product?.category?.name
                            : p?.node?.product?.category?.translation
                              ? p?.node?.product?.category?.translation?.name
                              : p?.node?.product?.category?.name
                        }
                        title={
                          locale === 'ru'
                            ? firstVariant?.name
                            : firstVariant.translation
                              ? firstVariant.translation.name
                              : firstVariant?.name
                        }
                        labels={labels}
                        availability={firstVariant?.availability}
                        pricePerMonth={
                          firstVariant?.pricing?.installmentPrice12?.amount || 0
                        }
                        price={firstVariant?.pricing?.price?.net?.amount || 0}
                        discount={
                          firstVariant?.pricing?.discount?.net?.amount ?? 0
                        }
                        priceUndiscounted={
                          firstVariant?.pricing?.priceUndiscounted?.net
                            ?.amount ?? 0
                        }
                        bonus={firstVariant?.pricing?.bonusesAmount}
                      />
                    );
                  })}
                </div>
              ))}
            {cardsView === 'full' &&
              (variantLoading || showSkeletons ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className={`${styles.listCards} ${styles.skeletonForCardSecond}`}
                  >
                    <Skeleton />
                  </div>
                ))
              ) : (
                <div className={styles.listCards}>
                  {(productsToRender?.length === 6
                    ? productsToRender.slice(
                        0,
                        Math.max(0, productsToRender.length - 2)
                      )
                    : productsToRender
                  ).map(p => {
                    const firstVariant = p?.node;
                    const labels =
                      firstVariant?.labels?.edges?.map(
                        (edge: any) => edge?.node
                      ) || [];

                    return (
                      <div key={p?.node?.id} className={styles.card}>
                        <CardSecond
                          id={firstVariant?.id}
                          categorySlug={p?.node?.product?.category?.slug}
                          slug={p?.node?.product?.slug}
                          productId={firstVariant?.product?.id}
                          images={firstVariant?.media}
                          reviews={p?.node?.product?.reviews?.totalCount}
                          sku={firstVariant?.sku}
                          title={
                            locale === 'ru'
                              ? firstVariant?.name
                              : firstVariant.translation
                                ? firstVariant.translation.name
                                : firstVariant?.name
                          }
                          bonus={firstVariant?.pricing?.bonusesAmount}
                          attributes={
                            p?.node?.product
                              ?.attributes as SelectedAttributeType[]
                          }
                          labels={labels}
                          pricePerMonth={
                            firstVariant?.pricing?.installmentPrice12?.amount
                          }
                          price={firstVariant?.pricing?.price?.net?.amount}
                          discount={
                            firstVariant?.pricing?.discount?.net?.amount
                          }
                          priceUndiscounted={
                            firstVariant?.pricing?.priceUndiscounted?.net
                              ?.amount
                          }
                          preOrder={false}
                          gift={false}
                        />
                      </div>
                    );
                  })}
                </div>
              ))}
            {variantLoading || showSkeletons ? (
              <div className={styles.mobileListCards}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className={styles.skeletonForCardSecond}>
                    <Skeleton />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.mobileListCards}>
                {selectedProductIds.length === 0
                  ? productsToRender?.slice(0, 4).map((p, index) => {
                      const firstVariant = p?.node;
                      const labels =
                        firstVariant?.labels?.edges?.map(
                          (edge: any) => edge?.node
                        ) || [];
                      const imageUrl = firstVariant?.media?.[0]?.url;
                      return (
                        <CategoryCard
                          id={firstVariant?.id}
                          categorySlug={p?.node?.product?.category?.slug}
                          slug={p?.node?.product?.slug}
                          productId={firstVariant?.product?.id}
                          key={firstVariant?.id}
                          image={imageUrl}
                          wide={true}
                          productType={
                            locale === 'ru'
                              ? p?.node?.product?.category?.name
                              : (p?.node?.product?.category?.translation
                                  ?.name ?? '')
                          }
                          title={
                            locale === 'ru'
                              ? firstVariant?.name
                              : firstVariant?.translation?.name
                          }
                          labels={labels}
                          availability={firstVariant?.availability}
                          pricePerMonth={
                            firstVariant?.pricing?.installmentPrice12?.amount ||
                            0
                          }
                          price={firstVariant?.pricing?.price?.net?.amount ?? 0}
                          discount={
                            firstVariant?.pricing?.discount?.net?.amount ?? 0
                          }
                          priceUndiscounted={
                            firstVariant?.pricing?.priceUndiscounted?.net
                              ?.amount ?? 0
                          }
                          bonus={firstVariant?.pricing?.bonusesAmount}
                        />
                      );
                    })
                  : productsVariants.slice(0, 4).map(p => {
                      const labels =
                        p?.labels?.edges?.map((edge: any) => edge?.node) || [];
                      const imageUrl = p?.media?.[0]?.url;
                      return (
                        <CategoryCard
                          id={p?.id}
                          categorySlug={p?.product?.category?.slug}
                          slug={p?.product?.slug}
                          productId={p?.product?.id}
                          key={p?.id}
                          image={imageUrl}
                          wide={true}
                          productType={
                            locale === 'ru'
                              ? p?.node?.product?.category?.name
                              : (p?.node?.product?.category?.translation
                                  ?.name ?? '')
                          }
                          title={
                            locale === 'ru' ? p?.name : p?.translation?.name
                          }
                          labels={labels}
                          availability={p?.availability}
                          pricePerMonth={p?.pricing?.installmentPrice12?.amount}
                          price={p?.pricing?.price?.net?.amount ?? 0}
                          discount={p?.pricing?.discount?.net?.amount ?? 0}
                          priceUndiscounted={
                            p?.pricing?.priceUndiscounted?.net?.amount ?? 0
                          }
                          bonus={p?.pricing?.bonusesAmount}
                        />
                      );
                    })}
              </div>
            )}

            <div className={styles.fullWidthCarousel}>
              <CustomCarouselCategory />
            </div>

            {cardsView === 'card' &&
              (variantLoading || showSkeletons ? (
                <div className={styles.cardsTypeList}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className={`${styles.skeletonForCardSecond}`}
                    >
                      <Skeleton />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.cardsTypeList}>
                  {productsToRender?.length > 8 &&
                    productsToRender
                      .slice(-(productsToRender.length - 8))
                      .map(p => {
                        const firstVariant = p?.node;
                        const labels =
                          firstVariant?.labels?.edges?.map(
                            (edge: any) => edge?.node
                          ) || [];
                        const imageUrl = firstVariant?.media?.[0]?.url;
                        return (
                          <CategoryCard
                            id={firstVariant?.id}
                            categorySlug={p?.node?.product?.category?.slug}
                            slug={p?.node?.product?.slug}
                            productId={firstVariant?.product?.id}
                            key={firstVariant?.id}
                            image={imageUrl}
                            wide={true}
                            productType={
                              locale === 'ru'
                                ? p?.node?.product?.category?.name
                                : p?.node?.product?.category?.translation?.name
                            }
                            title={
                              locale === 'ru'
                                ? firstVariant?.name
                                : firstVariant?.translation?.name
                            }
                            labels={labels}
                            availability={firstVariant?.availability}
                            pricePerMonth={
                              firstVariant?.pricing?.installmentPrice12
                                ?.amount || 0
                            }
                            price={
                              firstVariant?.pricing?.price?.net?.amount || 0
                            }
                            discount={
                              firstVariant?.pricing?.discount?.net?.amount ?? 0
                            }
                            priceUndiscounted={
                              firstVariant?.pricing?.priceUndiscounted?.net
                                ?.amount ?? 0
                            }
                            bonus={firstVariant?.pricing?.bonusesAmount}
                          />
                        );
                      })}
                </div>
              ))}
            {cardsView === 'full' &&
              (variantLoading || showSkeletons ? (
                Array.from({ length: 2 }).map((_, index) => (
                  <div
                    key={index}
                    className={`${styles.listCards} ${styles.skeletonForCardSecond}`}
                  >
                    <Skeleton />
                  </div>
                ))
              ) : (
                <div className={styles.listCards}>
                  {productsToRender.length === 6 &&
                    productsToRender?.slice(-2).map(p => {
                      const firstVariant = p?.node;
                      const labels =
                        firstVariant?.labels?.edges?.map(
                          (edge: any) => edge?.node
                        ) || [];

                      return (
                        <div key={p?.node?.id} className={styles.card}>
                          <CardSecond
                            id={firstVariant?.id}
                            categorySlug={p?.node?.product?.category?.slug}
                            slug={p?.node?.product?.slug}
                            productId={firstVariant?.product?.id}
                            images={firstVariant?.media}
                            reviews={p?.node?.product?.reviews?.totalCount}
                            sku={firstVariant?.sku}
                            title={firstVariant?.name}
                            bonus={firstVariant?.pricing?.bonusesAmount}
                            attributes={
                              p?.node?.product
                                ?.attributes as SelectedAttributeType[]
                            }
                            labels={labels}
                            price={firstVariant?.pricing?.price?.net?.amount}
                            discount={
                              firstVariant?.pricing?.discount?.net?.amount
                            }
                            priceUndiscounted={
                              firstVariant?.pricing?.priceUndiscounted?.net
                                ?.amount
                            }
                            pricePerMonth={
                              firstVariant?.pricing?.installmentPrice12?.amount
                            }
                            preOrder={false}
                            gift={false}
                          />
                        </div>
                      );
                    })}
                </div>
              ))}
            {variantLoading || showSkeletons ? (
              <div className={styles.mobileListCards}>
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className={styles.skeletonForCardSecond}>
                    <Skeleton />
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.mobileListCards}>
                {selectedProductIds.length === 0
                  ? data?.category?.products?.edges.slice(4, 6).map(p => {
                      const firstVariant = p?.node?.variants?.[0];
                      const labels =
                        firstVariant?.labels?.edges?.map(
                          (edge: any) => edge?.node
                        ) || [];
                      const imageUrl = firstVariant?.media?.[0]?.url;

                      return (
                        <CategoryCard
                          id={firstVariant?.id}
                          categorySlug={p?.node?.category?.slug}
                          slug={p?.node?.slug}
                          productId={firstVariant?.product?.id}
                          key={firstVariant?.id}
                          image={imageUrl}
                          wide={true}
                          productType={
                            locale === 'ru'
                              ? p?.node?.category?.name
                              : p?.node?.category?.translation?.name
                          }
                          title={
                            locale === 'ru'
                              ? firstVariant?.name
                              : firstVariant?.translation?.name
                          }
                          labels={labels}
                          availability={firstVariant?.availability}
                          pricePerMonth={
                            firstVariant?.pricing?.installmentPrice12?.amount ||
                            0
                          }
                          price={firstVariant?.pricing?.price?.net?.amount ?? 0}
                          discount={
                            firstVariant?.pricing?.discount?.net?.amount ?? 0
                          }
                          priceUndiscounted={
                            firstVariant?.pricing?.priceUndiscounted?.net
                              ?.amount ?? 0
                          }
                          bonus={firstVariant?.pricing?.bonusesAmount}
                        />
                      );
                    })
                  : productsVariants
                      .slice(
                        calculateBottomSlice().start,
                        calculateBottomSlice().end
                      )
                      .map(p => {
                        const labels =
                          p?.labels?.edges?.map((edge: any) => edge?.node) ||
                          [];
                        const imageUrl = p?.media?.[0]?.url;
                        return (
                          <CategoryCard
                            id={p?.id}
                            categorySlug={p?.product?.category?.slug}
                            slug={p?.product?.slug}
                            productId={p?.product?.id}
                            key={p?.id}
                            image={imageUrl}
                            wide={true}
                            productType={
                              locale === 'ru'
                                ? p?.product?.category?.name
                                : p?.product?.category?.translation?.name
                            }
                            title={
                              locale === 'ru' ? p?.name : p?.translation?.name
                            }
                            labels={labels}
                            availability={p?.availability}
                            pricePerMonth={
                              p?.pricing?.installmentPrice12?.amount
                            }
                            price={p?.pricing?.price?.net?.amount ?? 0}
                            discount={p?.pricing?.discount?.net?.amount ?? 0}
                            priceUndiscounted={
                              p?.pricing?.priceUndiscounted?.net?.amount ?? 0
                            }
                            bonus={p?.pricing?.bonusesAmount}
                          />
                        );
                      })}
              </div>
            )}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <Pagination
                  count={count}
                  setCount={setCount}
                  nextPage={nextPage}
                  prevPage={prevPage}
                  totalProducts={totalProducts}
                  limit={productsPerPage()}
                />
              </div>
            )}
            {paramSlug !== 'topsellers' ? (
              <SecondContentLiderSellCategory categoryId={categoryId} />
            ) : null}
          </div>
          <SecondContentFilter
            selectedProductIds={selectedProductIds}
            loading={productsLoading}
            totalCount={data?.category?.products?.totalCount || 0}
            displayedProductsLength={displayedProducts?.length || 0}
            productsVariantsLength={productsVariants?.length || 0}
            productsData={productsData?.products?.edges as ProductType[]}
            handleMiniCardClick={handleMiniCardClick}
            handleClearFilters={handleClearFilters}
            attributes={attributes as AttributeTypes[]}
            handleChangePrice={handleChangePrice}
            brandData={brandData as BrandType}
            handleChangeAttribute={handleChangeAttribute}
            selectedAttribute={selectedAttribute}
            isAvailableForInstallment={isAvailableForInstallment}
            setIsAvailableForInstallment={setIsAvailableForInstallment}
            isOnSale={isOnSale}
            setIsOnSale={setIsOnSale}
            handleChangeBrands={handleChangeBrands}
            percentDiscountAmount={percentDiscountAmount}
            onPercentDiscountAmount={handleChangePercentDiscountAmount}
            discountAmounts={discountAmounts}
          />
        </div>
      </div>
      <SecondContentDrawer
        openMobileVersion={openMobileVersion}
        setOpenMobileVersion={setOpenMobileVersion}
        closeMobileMenu={closeMobileMenu}
        handleChangePrice={handleChangePrice}
        brandData={brandData as BrandType}
        attributes={attributes as AttributeTypes[]}
        handleChangeAttribute={handleChangeAttribute}
        selectedAttribute={selectedAttribute}
        isAvailableForInstallment={isAvailableForInstallment}
        setIsAvailableForInstallment={setIsAvailableForInstallment}
        isOnSale={isOnSale}
        setIsOnSale={setIsOnSale}
        handleChangeBrands={handleChangeBrands}
        onPercentDiscountAmount={setPercentDiscountAmount}
      />
    </div>
  );
};

export default CategorySecondContent;
